// Upload Tracker Service - Persistent upload state management
class UploadTracker {
  constructor() {
    this.storageKey = 'bulk_upload_progress';
    this.listeners = new Set();
  }

  // Get current upload state
  getState() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to get upload state:', error);
      return null;
    }
  }

  // Start tracking an upload
  startUpload(uploadId, data) {
    const state = {
      uploadId,
      startTime: Date.now(),
      progress: 0,
      status: 'uploading',
      totalFiles: data.totalFiles || 0,
      processedFiles: 0,
      currentBatch: data.currentBatch || 1,
      totalBatches: data.totalBatches || 1,
      documentType: data.documentType,
      tourId: data.tourId,
      uploadMode: data.uploadMode || 'files',
      errors: [],
      warnings: []
    };

    console.log('[UploadTracker] startUpload:', state);
    this.setState(state);
    this.notifyListeners(state);
    return uploadId;
  }

  // Update upload progress
  updateProgress(progress, additionalData = {}) {
    const state = this.getState();
    if (!state) return;

    const updatedState = {
      ...state,
      progress: Math.min(100, Math.max(0, progress)),
      lastUpdate: Date.now(),
      ...additionalData
    };

    this.setState(updatedState);
    this.notifyListeners(updatedState);
  }

  // Mark upload as completed
  completeUpload(results) {
    const state = this.getState();
    if (!state) return;

    const completedState = {
      ...state,
      status: 'completed',
      progress: 100,
      endTime: Date.now(),
      results,
      duration: Date.now() - state.startTime
    };

    this.setState(completedState);
    this.notifyListeners(completedState);

    // Auto-clear completed uploads after 5 minutes
    setTimeout(() => {
      this.clearUpload();
    }, 5 * 60 * 1000);
  }

  // Mark upload as failed
  failUpload(error) {
    const state = this.getState();
    if (!state) return;

    const failedState = {
      ...state,
      status: 'failed',
      endTime: Date.now(),
      error: error.message || error,
      duration: Date.now() - state.startTime
    };

    this.setState(failedState);
    this.notifyListeners(failedState);

    // Auto-clear failed uploads after 10 minutes
    setTimeout(() => {
      this.clearUpload();
    }, 10 * 60 * 1000);
  }

  // Clear upload state
  clearUpload() {
    localStorage.removeItem(this.storageKey);
    this.notifyListeners(null);
  }

  // Subscribe to state changes
  subscribe(callback) {
    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  // Private methods
  setState(state) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save upload state:', error);
    }
  }

  notifyListeners(state) {
    this.listeners.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('Upload tracker listener error:', error);
      }
    });
  }

  // Format duration for display
  formatDuration(duration) {
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Generate unique upload ID
  generateUploadId() {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Create singleton instance
const uploadTracker = new UploadTracker();

export default uploadTracker;